// Super-Admin only — Backyard Membership management.
// View members, adjust credits, change validity, manually grant, cancel,
// and inspect the audit trail of credit transactions.
import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage } from './_shared';
import AppModal from '../../components/ui/AppModal';

const STATUS_FILTERS = [
  { value: '',         label: 'All' },
  { value: 'active',   label: 'Active' },
  { value: 'expired',  label: 'Expired' },
  { value: 'cancelled',label: 'Cancelled' },
];

const STATUS_COLORS = {
  active:    'bg-green-100 text-green-700',
  expired:   'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s || ''; }
};
const daysUntil = (s) => {
  try {
    const ms = new Date(s) - new Date();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch { return 0; }
};

export default function MembershipsTab() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch]     = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState(null); // 'credits' | 'validity' | 'cancel' | 'history'
  const [showGrant, setShowGrant] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const r = await axiosInstance.get('/admin/memberships', { params });
      setMemberships(r.data || []);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load memberships'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const totals = useMemo(() => ({
    count: memberships.length,
    active: memberships.filter((m) => m.status === 'active').length,
    creditsLive: memberships
      .filter((m) => m.status === 'active')
      .reduce((s, m) => s + (m.balance_remaining || 0), 0),
  }), [memberships]);

  const open = (m, kind) => { setActionTarget(m); setActionType(kind); };
  const close = () => { setActionTarget(null); setActionType(null); };

  return (
    <div className="space-y-3" data-testid="admin-memberships-tab">
      <button
        onClick={() => setShowGrant(true)}
        data-testid="grant-membership-btn"
        className="w-full py-3 bg-[#2B3A67] text-white font-extrabold rounded-2xl flex items-center justify-center gap-2"
      >
        <i className="ph-bold ph-gift" /> Grant Complimentary Membership
      </button>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-white rounded-2xl py-3 border border-gray-100">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total</p>
          <p className="text-2xl font-extrabold text-[#2B3A67]" data-testid="mship-stat-total">{totals.count}</p>
        </div>
        <div className="bg-white rounded-2xl py-3 border border-gray-100">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active</p>
          <p className="text-2xl font-extrabold text-green-600" data-testid="mship-stat-active">{totals.active}</p>
        </div>
        <div className="bg-white rounded-2xl py-3 border border-gray-100">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Live Credits</p>
          <p className="text-2xl font-extrabold text-[#E54562]" data-testid="mship-stat-credits">{fmtINR(totals.creditsLive)}</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => setFilterStatus(f.value)}
            data-testid={`mship-filter-${f.value || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap ${
              filterStatus === f.value ? 'bg-[#2B3A67] text-white' : 'bg-white border border-gray-100 text-gray-500'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <input
        data-testid="mship-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search user name or email"
        className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 text-sm focus:border-[#2B3A67] focus:outline-none"
      />

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" />
        </div>
      ) : memberships.length === 0 ? (
        <div className="text-center text-gray-400 py-10 text-sm">No memberships match the current filter.</div>
      ) : (
        <div className="space-y-3" data-testid="mship-list">
          {memberships.map((m) => (
            <MembershipRow key={m.id} m={m} onAction={open} />
          ))}
        </div>
      )}

      {/* Action modals */}
      {actionTarget && actionType === 'credits' && (
        <CreditsAdjustModal target={actionTarget} onClose={close} onDone={load} />
      )}
      {actionTarget && actionType === 'validity' && (
        <ValidityAdjustModal target={actionTarget} onClose={close} onDone={load} />
      )}
      {actionTarget && actionType === 'cancel' && (
        <CancelModal target={actionTarget} onClose={close} onDone={load} />
      )}
      {actionTarget && actionType === 'history' && (
        <HistoryModal target={actionTarget} onClose={close} />
      )}
      {showGrant && (
        <ManualGrantModal onClose={() => setShowGrant(false)} onDone={() => { setShowGrant(false); load(); }} />
      )}
    </div>
  );
}


/* ---------------- Row ---------------- */

function MembershipRow({ m, onAction }) {
  const total = m.credits_total || m.price || 0;
  const used = Math.max(total - m.balance_remaining, 0);
  const pct = total > 0 ? Math.max(0, Math.min(100, (m.balance_remaining / total) * 100)) : 0;
  const dLeft = daysUntil(m.expires_at);
  const expiringSoon = m.status === 'active' && dLeft >= 0 && dLeft <= 14;

  return (
    <div
      className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
      data-testid={`mship-row-${m.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="font-extrabold truncate" data-testid={`mship-user-${m.id}`}>{m.user_name || '—'}</p>
          <p className="text-xs text-gray-400 truncate">{m.user_email}</p>
        </div>
        <span className={`text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider whitespace-nowrap ${
          STATUS_COLORS[m.status] || 'bg-gray-100 text-gray-600'
        }`}>
          {m.status}
        </span>
      </div>

      <p className="text-xs font-bold text-[#2B3A67] mb-2">
        {m.plan_name} · ₹{m.price} paid
      </p>

      {/* Credits progress bar — Q5 */}
      <div className="mb-2">
        <div className="flex justify-between items-baseline text-xs mb-1">
          <span className="font-bold text-gray-500">
            <span className="text-[#E54562] font-black text-base" data-testid={`mship-balance-${m.id}`}>
              {fmtINR(m.balance_remaining)}
            </span>
            <span className="text-gray-400 ml-1">/ {fmtINR(total)}</span>
          </span>
          <span className="text-[10px] font-bold text-gray-400">
            {used > 0 ? `${fmtINR(used)} used` : 'unused'}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#E54562] to-[#F4D341] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-[11px]">
        <span className="text-gray-500">
          Purchased {fmtDate(m.purchased_at)} · Expires {fmtDate(m.expires_at)}
          {m.status === 'active' && (
            <span className={`ml-1 font-bold ${expiringSoon ? 'text-amber-600' : 'text-gray-400'}`}>
              ({dLeft >= 0 ? `${dLeft}d left` : 'expired'})
            </span>
          )}
        </span>
      </div>

      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap">
        <button
          onClick={() => onAction(m, 'credits')}
          data-testid={`mship-credits-${m.id}`}
          className="flex-1 min-w-[100px] px-3 py-2 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs rounded-xl"
        >
          Adjust Credits
        </button>
        <button
          onClick={() => onAction(m, 'validity')}
          data-testid={`mship-validity-${m.id}`}
          className="flex-1 min-w-[100px] px-3 py-2 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs rounded-xl"
        >
          Validity
        </button>
        <button
          onClick={() => onAction(m, 'history')}
          data-testid={`mship-history-${m.id}`}
          className="px-3 py-2 border-2 border-gray-300 text-gray-500 font-bold text-xs rounded-xl"
          title="Audit log"
        >
          <i className="ph-bold ph-clock-counter-clockwise" />
        </button>
        {m.status !== 'cancelled' && (
          <button
            onClick={() => onAction(m, 'cancel')}
            data-testid={`mship-cancel-${m.id}`}
            className="px-3 py-2 border-2 border-[#E54562] text-[#E54562] font-bold text-xs rounded-xl"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}


/* ---------------- Modals ---------------- */

function CreditsAdjustModal({ target, onClose, onDone }) {
  const [mode, setMode] = useState('add'); // add | deduct
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseInt(amount, 10);
    if (!n || n <= 0) { toast.error('Enter a positive amount'); return; }
    if (reason.trim().length < 3) { toast.error('Reason is required (min 3 chars)'); return; }
    setBusy(true);
    try {
      const delta = mode === 'add' ? n : -n;
      await axiosInstance.patch(`/admin/memberships/${target.id}/credits`, {
        delta, description: reason.trim(),
      });
      toast.success(`Credits ${mode === 'add' ? 'added' : 'deducted'}`);
      onDone(); onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    } finally { setBusy(false); }
  };

  return (
    <AppModal open onClose={onClose} title="Adjust Credits">
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-xl p-3 text-sm">
          <p className="font-bold">{target.user_name || target.user_email}</p>
          <p className="text-xs text-gray-500">
            Current balance: <b>{fmtINR(target.balance_remaining)}</b> / {fmtINR(target.credits_total || target.price)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setMode('add')}
            data-testid="credits-mode-add"
            className={`py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider ${
              mode === 'add' ? 'bg-emerald-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-500'
            }`}
          >
            Add
          </button>
          <button
            onClick={() => setMode('deduct')}
            data-testid="credits-mode-deduct"
            className={`py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider ${
              mode === 'deduct' ? 'bg-[#E54562] text-white' : 'bg-white border-2 border-gray-200 text-gray-500'
            }`}
          >
            Deduct
          </button>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Amount</label>
          <input
            data-testid="credits-amount"
            type="number" min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
            placeholder="e.g. 1000"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
            Reason <span className="text-[#E54562]">*</span>
          </label>
          <textarea
            data-testid="credits-reason"
            rows={2} required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Goodwill credit for booking issue #1234"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 border-2 border-gray-300 text-gray-500 font-bold rounded-2xl">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="credits-submit"
            className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl disabled:opacity-50"
          >
            {busy ? 'Saving...' : `Confirm ${mode === 'add' ? '+' : '-'}${amount || 0}`}
          </button>
        </div>
      </div>
    </AppModal>
  );
}


function ValidityAdjustModal({ target, onClose, onDone }) {
  const [presetDays, setPresetDays] = useState(null);
  const [exactDate,  setExactDate]  = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (presetDays == null && !exactDate) { toast.error('Pick a preset or a date'); return; }
    if (reason.trim().length < 3) { toast.error('Reason is required'); return; }
    setBusy(true);
    try {
      const payload = { description: reason.trim() };
      if (exactDate) payload.new_expires_at = new Date(exactDate + 'T23:59:59Z').toISOString();
      else payload.days_delta = presetDays;
      await axiosInstance.patch(`/admin/memberships/${target.id}/validity`, payload);
      toast.success('Validity updated');
      onDone(); onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    } finally { setBusy(false); }
  };

  const presets = [
    { d: 30,  label: '+30d' }, { d: 90, label: '+90d' }, { d: 365, label: '+1yr' },
    { d: -7, label: '-7d' },  { d: -30, label: '-30d' },
  ];

  return (
    <AppModal open onClose={onClose} title="Adjust Validity">
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-xl p-3 text-sm">
          <p className="font-bold">{target.user_name || target.user_email}</p>
          <p className="text-xs text-gray-500">Current expiry: <b>{fmtDate(target.expires_at)}</b></p>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Quick presets</label>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.d}
                onClick={() => { setPresetDays(p.d); setExactDate(''); }}
                data-testid={`validity-preset-${p.d}`}
                className={`px-3 py-2 rounded-full text-xs font-bold ${
                  presetDays === p.d && !exactDate
                    ? 'bg-[#2B3A67] text-white'
                    : 'bg-white border-2 border-gray-200 text-gray-500'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest">— or —</div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Pick exact date</label>
          <input
            data-testid="validity-date"
            type="date"
            value={exactDate}
            onChange={(e) => { setExactDate(e.target.value); setPresetDays(null); }}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
            Reason <span className="text-[#E54562]">*</span>
          </label>
          <textarea
            data-testid="validity-reason"
            rows={2} required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
            placeholder="e.g. Tournament postponed by 30 days"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 border-2 border-gray-300 text-gray-500 font-bold rounded-2xl">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="validity-submit"
            className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl disabled:opacity-50">
            {busy ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </AppModal>
  );
}


function CancelModal({ target, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error('Reason is required'); return; }
    setBusy(true);
    try {
      await axiosInstance.post(`/admin/memberships/${target.id}/cancel`, { description: reason.trim() });
      toast.success('Membership cancelled');
      onDone(); onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    } finally { setBusy(false); }
  };
  return (
    <AppModal open onClose={onClose} title="Cancel Membership">
      <div className="space-y-4">
        <div className="bg-red-50 border-2 border-red-100 text-red-700 rounded-xl p-3 text-sm">
          You're about to cancel <b>{target.user_email}</b>'s {target.plan_name}.
          They keep their {fmtINR(target.balance_remaining)} credits but the membership status flips to <b>cancelled</b>.
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
            Reason <span className="text-[#E54562]">*</span>
          </label>
          <textarea
            data-testid="cancel-reason"
            rows={2} required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
            placeholder="e.g. Customer requested refund — refer ticket #4567"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 border-2 border-gray-300 text-gray-500 font-bold rounded-2xl">Back</button>
          <button onClick={submit} disabled={busy} data-testid="cancel-submit"
            className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl disabled:opacity-50">
            {busy ? 'Cancelling...' : 'Cancel Membership'}
          </button>
        </div>
      </div>
    </AppModal>
  );
}


function HistoryModal({ target, onClose }) {
  const [txs, setTxs] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get(`/admin/memberships/${target.id}/transactions`);
        setTxs(r.data || []);
      } catch (e) { setTxs([]); toast.error('Failed to load history'); }
    })();
  }, [target.id]);

  return (
    <AppModal open onClose={onClose} title="Transaction History">
      {!txs ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E54562]" />
        </div>
      ) : txs.length === 0 ? (
        <div className="text-center text-gray-400 py-6 text-sm">No transactions yet.</div>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="mship-history-list">
          {txs.map((t) => (
            <div key={t.id} className="bg-white border border-gray-100 rounded-xl p-3 text-xs">
              <div className="flex justify-between items-start mb-1">
                <span className={`font-bold uppercase tracking-wider ${
                  t.amount > 0 ? 'text-emerald-600' : t.amount < 0 ? 'text-[#E54562]' : 'text-gray-500'
                }`}>
                  {t.transaction_type.replace('_', ' ')}
                </span>
                <span className={`font-black ${
                  t.amount > 0 ? 'text-emerald-600' : t.amount < 0 ? 'text-[#E54562]' : 'text-gray-400'
                }`}>
                  {t.amount > 0 ? '+' : ''}{t.amount}
                </span>
              </div>
              <p className="text-gray-700">{t.description || '—'}</p>
              <p className="text-[10px] text-gray-400 mt-1">
                {new Date(t.created_at).toLocaleString('en-IN')} · balance after: {t.balance_after}
              </p>
            </div>
          ))}
        </div>
      )}
    </AppModal>
  );
}


function ManualGrantModal({ onClose, onDone }) {
  const [planId, setPlanId] = useState('vibe');
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [pickedUser, setPickedUser] = useState(null);
  const [customCredits, setCustomCredits] = useState('');
  const [days, setDays] = useState(365);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);

  // Lazy user search via existing /admin/users endpoint (client-side filter)
  useEffect(() => {
    if (userSearch.trim().length < 2) { setUsers([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axiosInstance.get('/admin/users');
        if (!active) return;
        const q = userSearch.trim().toLowerCase();
        const matched = (r.data || []).filter((u) =>
          (u.name || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q),
        );
        setUsers(matched.slice(0, 8));
      } catch { /* silent */ }
      finally { if (active) setSearching(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [userSearch]);

  const submit = async () => {
    if (!pickedUser) { toast.error('Pick a user'); return; }
    if (planId === 'custom' && (!customCredits || parseInt(customCredits, 10) <= 0)) {
      toast.error('Custom plan needs a positive credits amount'); return;
    }
    if (reason.trim().length < 3) { toast.error('Reason is required'); return; }
    setBusy(true);
    try {
      const payload = {
        user_id: pickedUser.id,
        plan_id: planId,
        days_valid: parseInt(days, 10) || 365,
        description: reason.trim(),
      };
      if (planId === 'custom' || customCredits) payload.credits = parseInt(customCredits, 10);
      await axiosInstance.post('/admin/memberships/manual-grant', payload);
      toast.success(`Granted to ${pickedUser.email}`);
      onDone();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    } finally { setBusy(false); }
  };

  return (
    <AppModal open onClose={onClose} title="Grant Complimentary Membership">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">User</label>
          {pickedUser ? (
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
              <div>
                <p className="font-bold text-sm">{pickedUser.name}</p>
                <p className="text-xs text-gray-500">{pickedUser.email}</p>
              </div>
              <button onClick={() => { setPickedUser(null); setUserSearch(''); }} className="text-xs font-bold text-[#E54562]">Change</button>
            </div>
          ) : (
            <>
              <input
                data-testid="grant-user-search"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Type name or email..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
              />
              {searching && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
              {users.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl mt-1 max-h-48 overflow-y-auto">
                  {users.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => { setPickedUser(u); setUserSearch(''); setUsers([]); }}
                      data-testid={`grant-user-pick-${u.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="text-sm font-bold">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Plan</label>
          <select
            data-testid="grant-plan"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl text-sm"
          >
            <option value="vibe">Backyard Vibe Club (11,000 credits default)</option>
            <option value="inner">Backyard Inner Circle (23,000 credits default)</option>
            <option value="custom">Custom (one-off complimentary)</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
              Credits {planId !== 'custom' && '(override optional)'}
            </label>
            <input
              data-testid="grant-credits"
              type="number" min="0"
              value={customCredits}
              onChange={(e) => setCustomCredits(e.target.value)}
              placeholder={planId === 'vibe' ? '11000' : planId === 'inner' ? '23000' : 'e.g. 5000'}
              className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Valid for (days)</label>
            <input
              data-testid="grant-days"
              type="number" min="1"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
            Reason <span className="text-[#E54562]">*</span>
          </label>
          <textarea
            data-testid="grant-reason"
            rows={2} required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Influencer collab — campaign #2026Q1"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 border-2 border-gray-300 text-gray-500 font-bold rounded-2xl">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="grant-submit"
            className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl disabled:opacity-50">
            {busy ? 'Granting...' : 'Grant'}
          </button>
        </div>
      </div>
    </AppModal>
  );
}
